import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cn } from './helpers';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'aspect-square h-4 w-4 rounded-full border border-active-border text-primary-800 shadow focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className='flex items-center justify-center'>
        <Circle className='h-2.5 w-2.5 fill-primary-800' />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

type RadioIndicatorProps = React.ComponentPropsWithoutRef<'div'> & {
  checked?: boolean;
  check?: boolean;
  className?: string;
};

const RadioIndicator = React.forwardRef<HTMLDivElement, RadioIndicatorProps>(
  ({ checked = false, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-checked={checked}
        className={cn(
          'aspect-square h-4 bg-white border w-4 flex items-center justify-center transition-all rounded-full',
          // checked styles
          'data-[checked=true]:border-primary-600 data-[checked=true]:bg-primary-600',
          // unchecked styles
          'border-pale-border bg-white',
          className
        )}
        {...props}
      >
        {checked && <Circle className='size-2.5 stroke-0 fill-white' />}
      </div>
    );
  }
);
RadioIndicator.displayName = 'RadioIndicator';

export { RadioGroup, RadioGroupItem, RadioIndicator };
